import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  getCoreRowModel,
  RowSelectionState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import { RowData, TableOptions } from '@tanstack/table-core';
import DefaultTable from './DefaultTable';
import { DataTableProps } from './types';
import { createSelectionColumn } from './utils';

function resolveRowId<TData extends RowData>(
  item: TData,
  index: number,
  getRowId?: TableOptions<TData>['getRowId'],
): string {
  if (getRowId) {
    return String(getRowId(item, index));
  }

  return String(index);
}

function buildPageRowSelection<TData extends RowData>(
  data: TData[],
  selectedItems: TData[],
  getRowId?: TableOptions<TData>['getRowId'],
): RowSelectionState {
  const selectedIds = new Set(
    selectedItems.map((item, index) => resolveRowId(item, index, getRowId)),
  );
  const synced: RowSelectionState = {};

  data.forEach((item, index) => {
    const id = resolveRowId(item, index, getRowId);

    if (selectedIds.has(id)) {
      synced[id] = true;
    }
  });

  return synced;
}

function rowSelectionEquals(
  a: RowSelectionState,
  b: RowSelectionState,
): boolean {
  const keysA = Object.keys(a).filter((key) => a[key]);
  const keysB = Object.keys(b).filter((key) => b[key]);

  if (keysA.length !== keysB.length) {
    return false;
  }

  return keysA.every((key) => b[key]);
}

function DataTable<TData extends RowData>({
  data,
  columns,
  pagination,
  actions,
  options,
  onSelectionChange,
  persistSelection = false,
  selectedItems,
  ...rest
}: DataTableProps<TData>) {
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const isSyncingRef = useRef(false);
  const pendingUserSelectionRef = useRef(false);
  const selectedItemsRef = useRef(selectedItems);
  const getRowId = options?.getRowId;

  selectedItemsRef.current = selectedItems;

  const tableGetRowId = useCallback(
    (item: TData, index: number) => resolveRowId(item, index, getRowId),
    [getRowId],
  );

  const pageKey = useMemo(
    () =>
      data.map((item, index) => resolveRowId(item, index, getRowId)).join('|'),
    [data, getRowId],
  );

  const handleRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      pendingUserSelectionRef.current = true;
      setRowSelection(updater);
    },
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: tableGetRowId,
    onRowSelectionChange: handleRowSelectionChange,
    state: {
      rowSelection,
    },
  });

  useEffect(() => {
    if (!persistSelection) {
      return;
    }

    if (pendingUserSelectionRef.current) {
      pendingUserSelectionRef.current = false;
      return;
    }

    const synced = buildPageRowSelection(data, selectedItems ?? [], getRowId);

    if (!rowSelectionEquals(rowSelection, synced)) {
      isSyncingRef.current = true;
      setRowSelection(synced);
    }
  }, [selectedItems, pageKey, persistSelection, getRowId]);

  useEffect(() => {
    if (isSyncingRef.current) {
      isSyncingRef.current = false;
      return;
    }

    const pageSelection = table
      .getSelectedRowModel()
      .rows.map((row) => row.original);

    if (!persistSelection) {
      onSelectionChange?.(pageSelection);
      return;
    }

    const pageIds = new Set(
      data.map((item, index) => resolveRowId(item, index, getRowId)),
    );
    const fromOtherPages = (selectedItemsRef.current ?? []).filter(
      (item, index) => !pageIds.has(resolveRowId(item, index, getRowId)),
    );

    onSelectionChange?.([...fromOtherPages, ...pageSelection]);
  }, [rowSelection]);

  return (
    <DefaultTable
      {...rest}
      table={table}
      actions={actions}
      pagination={pagination}
    />
  );
}

export default Object.assign(DataTable, {
  createSelectionColumn,
});
